import BaseModel from "../models/BaseModel";
import type Page from "../models/Page";
import TreeViewModel from "../models/TreeviewModel";
import Logger from "../Logger";
import PageReference from "../models/PageReference";
import PdfPageReference from "../models/PdfPageReference";
import { PageTypes } from "../models/PageTypes";
import { showToast } from "../components/Toasts";

const logger = new Logger();

export default class PageTreeViewModel extends TreeViewModel {
  constructor(obj: BaseModel) {
    super(obj);
  }

  get typedObj(): Page {
    return this.obj as Page;
  }

  get isSelectable(): boolean {
    return this.typedObj.isBuildingPlan;
  }

  get isPdf() {
    return this.typedObj.documentType.type == PageTypes.pdf;
  }

  get isFolder(): boolean {
    if (this.isPdf && this._pdfNumPages == undefined) {
      this._childrenAreLoading = true;
      setTimeout(async () => {
        await this.typedObj.pdfFile.loadFromDatabaseAndActivate(this.typedObj.pdfFileId());
        const pdf = this.typedObj.pdfFile.pdf;
        if (pdf) {
          if (this._pdfNumPages != pdf.numPages) {
            this._pdfNumPages = pdf.numPages;
            this.typedObj.raiseOnChange("pdfNumPages");
            this.typedObj.raiseOnChange("childCollection");
          }
          this._childrenAreLoading = false;
        }
      }, 50);

      return this._childrenAreLoading || (this._pdfNumPages != undefined && this._pdfNumPages > 1);
    }

    if (this._pdfChildren && this._pdfChildren.length > 0) {
      return true;
    }

    return this._childrenAreLoading;
  }

  _pdfNumPages: number | undefined = undefined;
  _pdfChildren: Array<PageReference> | undefined = undefined;
  _childrenAreLoading = false;

  getWaitingForChildren(): boolean {
    return this._childrenAreLoading;
  }

  get childCollection(): Array<BaseModel> | undefined {
    if (this.typedObj.documentType.type == PageTypes.pdf) {
      if (this._pdfChildren == undefined) {
        // runs later async
        this._childrenAreLoading = true;
        this.loadPdfChildren(); // async !!
      }

      return this._pdfChildren;
    }

    return undefined;
  }

  async flattenPdfOutline(items: [any], childrenEx: Array<PageReference>) {
    for (let item of items) {
      if (item.items && item.items.length > 0) {
        await this.flattenPdfOutline(item.items, childrenEx);
      } else {
        // const xPage = new BaseModel({}, undefined);
        // xPage.name = item.title;

        const pdf = this.typedObj.pdfFile.pdf;
        const index = await pdf.getPageIndex(item.dest[0]);

        // const explicitDest = await pdf.getDestination(item.dest);

        const pageRef = new PdfPageReference({ pageNumber: index + 1, name: item.title }, this.typedObj);
        childrenEx.push(pageRef);
      }
    }
  }

  async loadPdfChildren() {
    const pdf = this.typedObj.pdfFile.pdf;

    if (pdf && pdf.numPages > 1 && !this._pdfChildren) {
      const childrenEx = new Array<PageReference>();
      //
      // setting "this._pdfChildren" early avoids multiple nested async loading
      //

      // getMetadata()
      const outline = await pdf.getOutline();
      const labels = await pdf.getPageLabels();

      let i = 1;
      if (labels) {
        labels?.map((label: string) => {
          childrenEx.push(new PdfPageReference({ pageNumber: i++, name: label }, this.typedObj));
        });
      } else if (outline) {
        await this.flattenPdfOutline(outline, childrenEx);
      } else {
        for (let i = 1; i <= pdf.numPages; i++) {
          childrenEx.push(new PdfPageReference({ pageNumber: i, name: `Seite ${i}` }, this.typedObj));
        }
      }

      this._pdfNumPages = childrenEx.length;
      this._pdfChildren = childrenEx;
      this._childrenAreLoading = false;

      this.typedObj.raiseOnChange("pdfPages");
      this.typedObj.raiseOnChange("childCollection");
    } else {
      this._childrenAreLoading = false;
    }
  }

  getTagNeeded(): boolean {
    return true;
  }

  getDisableEdit(): boolean {
    if (this.typedObj.isBuildingPlan) {
      return false;
    }

    if (this.isPdf) {
      return false;
    } else {
      return true;
    }
  }

  handleOpen(): boolean {
    if (this.isPdf) {
      const pdfFile = this.typedObj.pdfFile;

      if (!pdfFile.pdf) {
        // PDF not yet loaded. isFolder's setTimeout may already be loading it
        // (pageIsLoading=true). Either way, wait until the in-flight load settles,
        // then load if still needed, then open automatically — no second click required.
        // PdfFile.activate() now always resets pageIsLoading (try/finally), so this
        // setTimeout chain is guaranteed to terminate.
        const tryOpen = () => {
          if (pdfFile.pageIsLoading) {
            setTimeout(tryOpen, 50);
            return;
          }
          if (!pdfFile.pdf) {
            pdfFile.loadFromDatabaseAndActivate(this.typedObj.pdfFileId()).then(() => {
              if (pdfFile.pdf) {
                this._pdfNumPages = pdfFile.pdf.numPages;
                this.typedObj.raiseOnChange("pdfNumPages");
                this.handleOpen();
              } else {
                showToast({ text: "PDF konnte nicht geladen werden" }); // no itx
              }
            });
          } else {
            this._pdfNumPages = pdfFile.pdf.numPages;
            this.typedObj.raiseOnChange("pdfNumPages");
            this.handleOpen();
          }
        };
        tryOpen();
        return false;
      }

      var id = this.obj.id;

      // Open the Page directly — PdfListViewer will render it in an iframe
      // (same style as generated document lists). No PdfPageReference needed.

      if (state.activePanes.isOpen(id)) {
        state.activePanes.activatePane(id);
      } else {
        state.activePanes.loadPage(id);
      }
      return true;
    }

    if (this.isFolder) {
      return false;
    }

    if (state.activePanes.isOpen(this.obj.id)) {
      state.activePanes.activatePane(this.obj.id);
    } else {
      state.activePanes.loadPage(this.obj.id);
    }
    return true;
  }

  getButtons(): JSX.Element[] | undefined {
    return [
      super.inlineButton_Open("inlineButton.openDocument"), // no itx
    ];
  }

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      items: [
        this.baseContextMenuItems_Delete(),
        // {
        //   id: "open",
        //   iconName: "edit",
        //   label: "Zeichung öffnen",
        //   onClick: () => this.handleOpen(),
        // },
        // this.baseContextMenuItems_Properties(),
      ],
    };
  }

  allowDrag(): boolean {
    if ((this.obj as Page)?.documentType.type == PageTypes.pdf) {
      // if it is a pdf-> can be dragged as backgroundpicture of room
      return true;
    }
    return false;
  }
}
